// A fast upload reports 100% and moves on to the next page in the same moment, which would cut the pile
// off part way through drawing that last real progress. The upload screen's bucket reports here whether
// its pile has caught up, and the upload flow waits for it (briefly) before moving on.

/** Never hold the hand-off longer than this, even if the pile cannot finish (a background tab has no frames). */
export const UPLOAD_BUCKET_CATCH_UP_MAX_WAIT_MS = 1000;

type MountedUploadBucket = { isPileCaughtUp: boolean };

const mountedUploadBuckets = new Set<MountedUploadBucket>();
const waitersForCatchUp = new Set<() => void>();

function areAllUploadBucketsCaughtUp() {
  for (const bucket of mountedUploadBuckets) {
    if (!bucket.isPileCaughtUp) return false;
  }
  return true;
}

function releaseWaitersIfCaughtUp() {
  if (!areAllUploadBucketsCaughtUp()) return;
  const waiters = [...waitersForCatchUp];
  waitersForCatchUp.clear();
  waiters.forEach((release) => release());
}

/** Called by an upload screen's bucket while it is mounted. */
export function registerUploadBucket() {
  const bucket: MountedUploadBucket = { isPileCaughtUp: true };
  mountedUploadBuckets.add(bucket);
  return {
    reportPileCaughtUp(isPileCaughtUp: boolean) {
      bucket.isPileCaughtUp = isPileCaughtUp;
      releaseWaitersIfCaughtUp();
    },
    unregister() {
      mountedUploadBuckets.delete(bucket);
      releaseWaitersIfCaughtUp();
    },
  };
}

/**
 * Resolves once every upload bucket on screen shows the latest progress, or after
 * UPLOAD_BUCKET_CATCH_UP_MAX_WAIT_MS. Resolves at once when no upload bucket is on screen.
 */
export function waitForUploadBucketToCatchUp(): Promise<void> {
  if (mountedUploadBuckets.size === 0) return Promise.resolve();
  return new Promise((resolve) => {
    let hasResolved = false;
    const release = () => {
      if (hasResolved) return;
      hasResolved = true;
      clearTimeout(maximumWaitTimeoutId);
      waitersForCatchUp.delete(release);
      resolve();
    };
    const maximumWaitTimeoutId = setTimeout(release, UPLOAD_BUCKET_CATCH_UP_MAX_WAIT_MS);
    // The progress reported just before this call reaches the pile once the loader renders it, so check
    // after the next frame rather than now, when the pile may still look caught up with older progress.
    const checkAfterLatestProgressReachedPile = () => {
      if (hasResolved) return;
      waitersForCatchUp.add(release);
      releaseWaitersIfCaughtUp();
    };
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => setTimeout(checkAfterLatestProgressReachedPile, 0));
    } else {
      setTimeout(checkAfterLatestProgressReachedPile, 0);
    }
  });
}
